import { memo, useCallback, useState } from "react";
import Accordion from "../accordian";
import { useTranslation } from "react-i18next";
import {
  allowedFileTypes,
  ContentType,
  PC_IN_OUT_PERMIT_ATTACHMENT_KEYS,
  PC_IN_OUT_PERMIT_ATTACHMENTS_TYPE,
  PCInOutPermitRequestDetails,
} from "../../constants/common";
import FileUploadInput from "../inputs/FileUploadInput";
import toast from "react-hot-toast";
import ViewAttachmentModal from "../modal/ViewAttachmentModal";
import { useGetPCPermitFileByType } from "../../hooks/permit/mutation";

interface PCInOutPermitAttachmentsCardProps {
  control: any;
  errors: any;

  isAttachments: {
    haveEmployeeId: number;
  };
  uploadRefs: Record<string, React.MutableRefObject<HTMLInputElement | null>>;
  fileNames: Record<string, string>;
  setFileName: (key: string, value: React.SetStateAction<string>) => void;

  permitDetails?: PCInOutPermitRequestDetails;

  isFieldsDisabled?: boolean;
}

const PCInOutPermitAttachmentsCard = ({
  control,
  errors,
  isAttachments,
  uploadRefs,
  fileNames,
  setFileName,
  permitDetails,
  isFieldsDisabled,
}: PCInOutPermitAttachmentsCardProps) => {
  const { t } = useTranslation();

  const [selectedKey, setSelectedKey] = useState<string>("1");

  const [previewFiles, setPreviewFiles] = useState<
    Record<number, { url: string; contentType: string }>
  >({});
  const [imageSrc, setImageSrc] = useState("");
  const [contentType, setContentType] = useState("");
  const [isViewAttachmentModalOpen, setIsViewAttachmentModalOpen] =
    useState(false);
  const [selectedType, setSelectedType] = useState<number>(0);
  const [isGetFileByTypeLoading, setIsGetFileByTypeLoading] = useState(false);

  const {
    mutateAsync: GetPCPermitFileByType,
    isPending: isGetPCPermitFileByTypeLoading,
  } = useGetPCPermitFileByType();

  const handleAttachmentView = useCallback(
    async (type: number) => {
      try {
        // If we already have a local preview for this attachment type
        if (previewFiles && previewFiles[type]) {
          setImageSrc(previewFiles[type].url);
          setContentType(previewFiles[type].contentType);
          setIsViewAttachmentModalOpen(true);
          return;
        }

        if (permitDetails?.id) {
          setSelectedType(type);

          const body = {
            Id: permitDetails?.id,
            Type: type,
          };

          const res = await GetPCPermitFileByType(body);

          setIsGetFileByTypeLoading(isGetPCPermitFileByTypeLoading);

          if (res) {
            let url = "";
            if (res?.contentType !== ContentType.PDF) {
              url = `data:${res?.contentType};base64,${res?.photoFile}`;
            } else {
              const binaryString = atob(res?.photoFile);
              const byteArray = new Uint8Array(binaryString.length);

              for (let i = 0; i < binaryString.length; i++) {
                byteArray[i] = binaryString.charCodeAt(i);
              }

              const blob = new Blob([byteArray], { type: ContentType.PDF });
              url = URL.createObjectURL(blob);
            }
            setImageSrc(url);
            setContentType(res?.contentType);
            setIsViewAttachmentModalOpen(true);
          }
        }
      } catch (error) {
        toast.error("Something went wrong");
      } finally {
        setSelectedType(0);
      }
    },
    [
      GetPCPermitFileByType,
      isGetPCPermitFileByTypeLoading,
      permitDetails?.id,
      previewFiles,
    ],
  );

  return (
    <>
      <div className="bg-white z-10 p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
        <Accordion
          id="1"
          selectedKey={selectedKey}
          onSelectionChange={setSelectedKey}
          title={
            <h1 className="text-xl md:text-2xl flex flex-1 -mt-2 items-center">
              {t("2")}. {t("attachments")}
            </h1>
          }
        >
          <div className="flex flex-col gap-2 lg:gap-4">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
              <FileUploadInput
                name="uploadEmployeeIDCard"
                label={t("uploadEmployeeIDCard")}
                accept=".png,.jpg,.jpeg,.pdf"
                allowedTypes={allowedFileTypes}
                isAttachments={isAttachments?.haveEmployeeId}
                control={control}
                uploadRef={
                  uploadRefs[PC_IN_OUT_PERMIT_ATTACHMENT_KEYS.EMPLOYEE_ID_CARD]
                }
                fileName={
                  fileNames[PC_IN_OUT_PERMIT_ATTACHMENT_KEYS.EMPLOYEE_ID_CARD]
                }
                setFileName={(val) =>
                  setFileName(
                    PC_IN_OUT_PERMIT_ATTACHMENT_KEYS.EMPLOYEE_ID_CARD,
                    val,
                  )
                }
                handleAttachmentView={handleAttachmentView}
                selectedType={selectedType}
                attachmentType={
                  PC_IN_OUT_PERMIT_ATTACHMENTS_TYPE.EMPLOYEE_ID_CARD
                }
                isGetFileByTypeLoading={isGetFileByTypeLoading}
                error={errors.uploadEmployeeIDCard}
                previewFiles={previewFiles}
                setPreviewFiles={setPreviewFiles}
                disabled={isFieldsDisabled}
                isRemovable={false}
              />
            </div>
          </div>
        </Accordion>
      </div>

      <ViewAttachmentModal
        attachment={imageSrc}
        contentType={contentType}
        isOpen={isViewAttachmentModalOpen}
        onOpenChange={() => {
          setIsViewAttachmentModalOpen(false);
          setImageSrc("");
        }}
      />
    </>
  );
};

export default memo(PCInOutPermitAttachmentsCard);
